import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  DefaultButton,
  Dropdown,
  IDropdownOption,
  Icon,
  MessageBar,
  MessageBarType,
  SearchBox,
  Spinner,
  Stack,
  Text,
  mergeStyleSets,
} from '@fluentui/react';
import moment from 'moment';
import { toastError } from '../../utils/toast';

/** Eén regel uit het maillog: verstuurd of mislukt. */
interface MailLogEntry {
  id: string;
  date: string;
  status: 'sent' | 'failed';
  /** Enkel bij een mislukking: in welke stap het faalde. */
  stage: string | null;
  /** Korte melding voor in de lijst. */
  summary: string | null;
  /** Volledige melding; enkel achter "Technische details". */
  message: string | null;
  /** Volledige oorzaak van Microsoft/Azure; enkel op verzoek tonen. */
  technical: string | null;
  from: string | null;
  to: string[];
  subject: string | null;
  attachments: string[];
  /** Waar de mail bij hoorde: Bestelbon, Billit-factuur, Klantfactuur, Min-/meerwerk. */
  kind: string | null;
  reference: string | null;
  constructionSite: string | null;
  relation: string | null;
  purchase_order_id: number | null;
  billit_invoice_id: number | null;
  customer_invoice_id: number | null;
  customer_contract_update_id: number | null;
}

/** De fasen zoals de backend ze onderscheidt, in mensentaal. */
const FASE_LABEL: Record<string, string> = {
  attachments: 'Bijlagen',
  auth: 'Aanmelden bij Microsoft',
  graph: 'Versturen',
  sender: 'Afzender niet toegelaten',
  unknown: 'Onbekend',
};

const STATUSSEN: IDropdownOption[] = [
  { key: 'all', text: 'Alles' },
  { key: 'sent', text: 'Enkel verstuurd' },
  { key: 'failed', text: 'Enkel mislukt' },
];

const styles = mergeStyleSets({
  kaart: {
    border: '1px solid #edebe9',
    borderRadius: 2,
    background: '#fff',
    padding: '12px 16px',
  },
  verstuurd: { borderLeft: '3px solid #71BF43' },
  mislukt: { borderLeft: '3px solid #ff5757' },
  labelOk: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    color: '#4a7c2a',
    background: 'rgba(113, 191, 67, 0.14)',
    padding: '2px 8px',
    borderRadius: 10,
  },
  labelFout: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    color: '#a4262c',
    background: 'rgba(255, 87, 87, 0.12)',
    padding: '2px 8px',
    borderRadius: 10,
  },
  tijd: { fontSize: 12, color: '#605e5c' },
  waarvoor: { fontWeight: 600, fontSize: 14 },
  context: { fontSize: 12, color: '#605e5c' },
  regel: { fontSize: 13 },
  bijlage: {
    fontSize: 11,
    background: '#f3f2f1',
    padding: '2px 8px',
    borderRadius: 2,
    marginRight: 6,
    display: 'inline-block',
    marginTop: 4,
  },
  melding: {
    fontSize: 13,
    color: '#a4262c',
    background: 'rgba(255, 87, 87, 0.07)',
    border: '1px solid rgba(255, 87, 87, 0.2)',
    borderRadius: 2,
    padding: '6px 10px',
    marginTop: 2,
  },
  technisch: {
    fontSize: 11,
    color: '#605e5c',
    background: '#faf9f8',
    padding: 10,
    borderRadius: 2,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: 6,
  },
});

/** "Bestelbon 1427.26" — valt terug op het id wanneer het stuk niet meer bestaat. */
const herkomst = (item: MailLogEntry): string => {
  if (item.kind) return `${item.kind} ${item.reference || ''}`.trim();
  if (item.purchase_order_id) return `Bestelbon #${item.purchase_order_id}`;
  if (item.billit_invoice_id) return `Billit-factuur #${item.billit_invoice_id}`;
  if (item.customer_invoice_id) return `Klantfactuur #${item.customer_invoice_id}`;
  if (item.customer_contract_update_id) return `Min-/meerwerk #${item.customer_contract_update_id}`;
  return 'Losse mail';
};

const MailLogCard: React.FC<{ item: MailLogEntry }> = ({ item }) => {
  const [toonDetails, setToonDetails] = useState(false);
  const mislukt = item.status === 'failed';
  const context = [item.constructionSite, item.relation].filter(Boolean).join(' · ');

  return (
    <div className={`${styles.kaart} ${mislukt ? styles.mislukt : styles.verstuurd}`}>
      <Stack tokens={{ childrenGap: 6 }}>
        <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
          <span className={mislukt ? styles.labelFout : styles.labelOk}>
            {mislukt ? FASE_LABEL[item.stage || 'unknown'] || item.stage : 'Verstuurd'}
          </span>
          <span className={styles.tijd}>
            {moment(item.date).format('DD/MM/YYYY HH:mm')}
          </span>
        </Stack>

        <Stack>
          <span className={styles.waarvoor}>{herkomst(item)}</span>
          {context && <span className={styles.context}>{context}</span>}
        </Stack>

        {item.subject && <span className={styles.regel}>{item.subject}</span>}
        <span className={styles.context}>
          {item.from || '?'} → {item.to.join(', ') || 'geen ontvangers'}
        </span>

        {item.attachments.length > 0 && (
          <div>
            {item.attachments.map(naam => (
              <span key={naam} className={styles.bijlage}>
                <Icon iconName='Attach' styles={{ root: { fontSize: 10, marginRight: 4 } }} />
                {naam}
              </span>
            ))}
          </div>
        )}

        {mislukt && (item.summary || item.message) && (
          <div className={styles.melding}>{item.summary || item.message}</div>
        )}

        {mislukt && (item.technical || item.message) && (
          <div>
            <DefaultButton
              iconProps={{ iconName: toonDetails ? 'ChevronUp' : 'ChevronDown' }}
              onClick={() => setToonDetails(!toonDetails)}
              styles={{ root: { height: 26, fontSize: 12, padding: '0 8px' } }}
            >
              Technische details
            </DefaultButton>
            {toonDetails && (
              <div className={styles.technisch}>
                {[item.message, item.technical].filter(Boolean).join('\n\n')}
              </div>
            )}
          </div>
        )}
      </Stack>
    </div>
  );
};

const MailLog: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [entries, setEntries] = useState<MailLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>('all');
  const [zoek, setZoek] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/mails/log?status=${status}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setEntries(await response.json());
    } catch (error) {
      toastError('Ophalen van het maillog is mislukt.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    document.title = '3bouw | Maillog';
  }, []);

  // Zoeken gebeurt op wat er al opgehaald is: meestal zoek je één bepaalde mail
  // waarvan iemand vraagt of hij vertrokken is.
  const zichtbaar = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter(e => [
      e.subject, e.from, e.reference, e.constructionSite, e.relation, e.kind,
      ...e.to,
    ].some(v => String(v || '').toLowerCase().includes(term)));
  }, [entries, zoek]);

  const aantalMislukt = zichtbaar.filter(e => e.status === 'failed').length;

  return (
    <Stack
      tokens={{ childrenGap: 16 }}
      styles={{
        root: {
          padding: 24,
          maxWidth: 1100,
          margin: '0 auto',
          width: '100%',
        },
      }}
    >
      <Stack horizontal horizontalAlign='space-between' verticalAlign='end' wrap>
        <Stack>
          <Text variant='xLarge' styles={{ root: { fontWeight: 600 } }}>Maillog</Text>
          <Text styles={{ root: { color: '#605e5c' } }}>
            Mails van de laatste 24 uur: wat verstuurd is en wat misliep.
          </Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign='end'>
          <Dropdown
            selectedKey={status}
            options={STATUSSEN}
            styles={{ root: { width: 150 } }}
            onChange={(_, option) => option && setStatus(String(option.key))}
          />
          <DefaultButton iconProps={{ iconName: 'Refresh' }} onClick={load} disabled={loading}>
            Vernieuwen
          </DefaultButton>
        </Stack>
      </Stack>

      <SearchBox
        placeholder='Zoek op onderwerp, werf, leverancier, afzender of ontvanger'
        value={zoek}
        onChange={(_, waarde) => setZoek(waarde || '')}
        onClear={() => setZoek('')}
      />

      {loading && <Spinner label='Laden...' />}

      {!loading && zichtbaar.length === 0 && (
        <MessageBar messageBarType={MessageBarType.info}>
          {zoek
            ? 'Geen mails gevonden voor deze zoekterm.'
            : 'Geen mails in de laatste 24 uur.'}
        </MessageBar>
      )}

      {!loading && zichtbaar.length > 0 && (
        <Stack tokens={{ childrenGap: 10 }}>
          <Text styles={{ root: { color: '#605e5c', fontSize: 13 } }}>
            {zichtbaar.length} mail{zichtbaar.length === 1 ? '' : 's'}
            {aantalMislukt > 0 && `, waarvan ${aantalMislukt} mislukt`}
          </Text>
          {zichtbaar.map(item => <MailLogCard key={item.id} item={item} />)}
        </Stack>
      )}
    </Stack>
  );
};

export default MailLog;
